import { useCallback, useMemo } from "react"
import { useAppDispatch, useAppSelector } from "../../../store/app/hooks"
import {
  setOpenAlert,
  setLoadingDelete,
  setCloseAlert,
} from "../../../store/features/alert/alertSlice"
import { useMutation, useQuery } from "@apollo/client"
import {
  selectSegmentationPagination,
  selectSegmentationQuery,
  setSegmentationPagination,
  setSegmentationQuery,
} from "../../../store/features/segmentation/segmentationSlice"
import { graphql } from "../../../gql"
import CustomTable from "../../Common/CustomTable/CustomTable"
import Loader from "../../Common/Loader/Loader"
import { getErrorsAsString } from "../../../utils/getErrorsAsString"
import { Segmentation, SortOrder } from "../../../gql/graphql"
import { MRT_ColumnDef } from "material-react-table"
import { Pagination } from "../../../types/Pagination"
import { setOpenSnackbar } from "../../../store/features/snackbar/snackbarSlice"
import { updateCacheSegmentation } from "../../../caches/updateCacheSegmentation"
import { useNavigate } from "react-router-dom"
import { setTitle } from "../../../store/features/menu/menuSlice"
import { havePermissions } from "../../../utils/permissions"
import useLang from "../../../hooks/useLang"

export const LIST_SEGMENTATIONS = graphql(`
  query segmentations_total(
    $where: SegmentationWhereInput
    $orderBy: [SegmentationOrderByWithRelationAndSearchRelevanceInput!]
    $take: Int
    $skip: Int
  ) {
    aggregateSegmentation {
      _count {
        _all
      }
    }
    segmentations(where: $where, orderBy: $orderBy, take: $take, skip: $skip) {
      id
      name_fr
      name_en
    }
  }
`)

export const DELETE_SEGMENTATION = graphql(`
  mutation DeleteOneSegmentation($where: SegmentationWhereUniqueInput!) {
    deleteOneSegmentation(where: $where) {
      id
      name_fr
      name_en
    }
  }
`)

const ListSegmentation = () => {
  const { page, size } = useAppSelector(selectSegmentationPagination)
  const query = useAppSelector(selectSegmentationQuery)
  const dispatch = useAppDispatch()
  const { getText } = useLang()

  const navigate = useNavigate()

  const queryName = useCallback(() => {
    return query
      ? {
          where: {
            OR: [
              { name_fr: { search: query } },
              { name_en: { search: query } },
              {
                id: {
                  equals: Number(query),
                },
              },
              {
                oldID: {
                  equals: Number(query),
                },
              },
            ],
          },
        }
      : {}
  }, [query])

  const { loading, data, refetch } = useQuery(LIST_SEGMENTATIONS, {
    variables: {
      take: size,
      skip: page * size,
      orderBy: { name_fr: SortOrder.Asc },
      ...queryName(),
    },
  })

  const [deleteSegmentation] = useMutation(DELETE_SEGMENTATION)

  const columns = useMemo<MRT_ColumnDef<Segmentation>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        enableEditing: false,
        size: 80,
        enableClickToCopy: true,
      },
      {
        accessorKey: "name_fr",
        header: getText("Nom en FR"),
        enableClickToCopy: true,
      },
      {
        accessorKey: "name_en",
        header: getText("Nom en EN"),
        enableClickToCopy: true,
      },
    ],
    [getText],
  )

  const handleChangePagination = (pagination: Pagination) =>
    dispatch(setSegmentationPagination(pagination))

  const handleValidDelete = (id: number) => {
    dispatch(setLoadingDelete(true))
    deleteSegmentation({
      variables: {
        where: {
          id,
        },
      },
      onCompleted: () => {
        dispatch(
          setOpenSnackbar({
            message: getText("La segmentation a été supprimée avec succès"),
            status: "success",
          }),
        )
        dispatch(setLoadingDelete(false))
        dispatch(setCloseAlert())
      },
      onError: (err) => {
        const message = getErrorsAsString(err)
        dispatch(setCloseAlert())
        dispatch(setOpenSnackbar({ message }))
      },
      update: (cache, { data }) => {
        updateCacheSegmentation({
          action: "delete",
          cache,
          entryData: data?.deleteOneSegmentation,
        })
      },
    })
  }

  const handleOpenDeleteDialog = (id: number) => {
    dispatch(
      setOpenAlert({
        handleValid: () => handleValidDelete(id),
        message: getText(
          "Êtes-vous vraiment sûr de vouloir supprimer cette segmentation ?",
        ),
        isLoading: false,
      }),
    )
  }

  const onChangeSearchValue = (value: string) =>
    dispatch(setSegmentationQuery(value))

  const handleEdit = (id: number) => {
    dispatch(setTitle(getText("Modifier une segmentation")))
    navigate(`/segmentation/edit/${id}`)
  }

  const handleView = (id: number) => {
    dispatch(setTitle("Detail d'une segmentation"))
    navigate(`/segmentation/detail/${id}`)
  }

  const handleRefresh = () => {
    window.location.reload()
  }

  if (loading && !query && !page) return <Loader />

  return (
    <CustomTable
      columns={columns}
      data={data?.segmentations || []}
      lableAddNew={getText("Créer une nouvelle segmentation")}
      rootLisName={"/segmentation/add"}
      isLoading={loading}
      showProgressBars={loading}
      rowCount={data?.aggregateSegmentation._count?._all || 0}
      handleChangePagination={handleChangePagination}
      pageIndex={page}
      pageSize={size}
      searchValue={query}
      onChangeSearchValue={onChangeSearchValue}
      handleDelete={
        havePermissions(["segmentation.delete"])
          ? handleOpenDeleteDialog
          : undefined
      }
      isHaveHeaderAction={havePermissions(["segmentation.create"])}
      handleEdit={
        havePermissions(["segmentation.change"]) ? handleEdit : undefined
      }
      isHaveView={havePermissions(["segmentation.view"])}
      handleView={
        havePermissions(["segmentation.view"]) ? handleView : undefined
      }
      isHaveAction={havePermissions([
        "segmentation.view",
        "segmentation.change",
        "segmentation.delete",
      ])}
      handleRefresh={handleRefresh}
    />
  )
}

export default ListSegmentation
